/**
 * TeamSettings.js
 * Page for managing team members and settings
 */

import React, { useState } from "react";
import { useTeam } from "../context/TeamContext";
import DataService from "../services/DataService";
import {
  FaUser,
  FaTrash,
  FaPlus,
  FaCrown,
  FaEdit,
  FaEye,
} from "react-icons/fa";

export default function TeamSettings() {
  const { currentTeam, permissions, refreshTeams, createTeam } = useTeam();
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("reader");
  const [adding, setAdding] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamDescription, setNewTeamDescription] = useState("");
  const [creatingTeam, setCreatingTeam] = useState(false);

  const dataService = new DataService();

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      alert("Please enter a team name");
      return;
    }

    try {
      setCreatingTeam(true);
      await createTeam(newTeamName, newTeamDescription);
      setShowCreateTeamModal(false);
      setNewTeamName("");
      setNewTeamDescription("");
      alert("Team created successfully!");
    } catch (error) {
      console.error("Error creating team:", error);
      alert("Failed to create team: " + error.message);
    } finally {
      setCreatingTeam(false);
    }
  };

  if (!permissions.canManage) {
    return (
      <div className="team-settings-page">
        <div className="access-denied-message">
          <h2>Access Denied</h2>
          <p>You need owner permissions to manage team settings.</p>
        </div>
      </div>
    );
  }

  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) {
      alert("Please enter an email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMemberEmail)) {
      alert("Please enter a valid email address");
      return;
    }

    try {
      setAdding(true);
      await dataService.addTeamMember(
        currentTeam.id,
        newMemberEmail,
        newMemberRole
      );
      await refreshTeams();
      setShowAddMemberModal(false);
      setNewMemberEmail("");
      setNewMemberRole("reader");
      alert("Member added successfully!");
    } catch (error) {
      console.error("Error adding member:", error);
      alert("Failed to add member: " + error.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (email, role) => {
    if (!window.confirm(`Remove ${email} from the team?`)) {
      return;
    }

    try {
      await dataService.removeTeamMember(currentTeam.id, email);
      await refreshTeams();
      alert("Member removed successfully");
    } catch (error) {
      console.error("Error removing member:", error);
      alert("Failed to remove member");
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "owner":
        return <FaCrown className="role-icon owner" />;
      case "creator":
        return <FaEdit className="role-icon creator" />;
      case "reader":
        return <FaEye className="role-icon reader" />;
      default:
        return <FaUser className="role-icon" />;
    }
  };

  const getRoleBadgeClass = (role) => {
    return `role-badge role-${role}`;
  };

  return (
    <div className="team-settings-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Team Settings</h1>
          <p className="page-subtitle">Manage {currentTeam.teamName}</p>
        </div>
        <button
          onClick={() => setShowCreateTeamModal(true)}
          className="btn btn-primary"
        >
          <FaPlus /> Create New Team
        </button>
      </div>

      {/* Team Info */}
      <div className="settings-section">
        <h2>Team Information</h2>
        <div className="info-card">
          <div className="info-row">
            <label>Team Name</label>
            <span>{currentTeam.teamName}</span>
          </div>
          {currentTeam.description && (
            <div className="info-row">
              <label>Description</label>
              <span>{currentTeam.description}</span>
            </div>
          )}
          <div className="info-row">
            <label>Created</label>
            <span>
              {new Date(
                currentTeam.createdAt?.toDate?.() || currentTeam.createdAt
              ).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div className="settings-section">
        <div className="section-header-with-action">
          <h2>Team Members</h2>
          <button
            onClick={() => setShowAddMemberModal(true)}
            className="btn btn-primary"
          >
            <FaPlus /> Add Member
          </button>
        </div>

        {/* Owners */}
        <div className="members-group">
          <h3>Owners</h3>
          <div className="members-list">
            {currentTeam.owners &&
              currentTeam.owners.map((uid) => (
                <div key={uid} className="member-card">
                  {getRoleIcon("owner")}
                  <div className="member-info">
                    <div className="member-id">{uid}</div>
                    <span className={getRoleBadgeClass("owner")}>Owner</span>
                  </div>
                  <div className="member-actions">
                    {/* Cannot remove owners from team settings (would need special UI) */}
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Creators */}
        {currentTeam.creators && currentTeam.creators.length > 0 && (
          <div className="members-group">
            <h3>Creators</h3>
            <div className="members-list">
              {currentTeam.creators.map((email) => (
                <div key={email} className="member-card">
                  {getRoleIcon("creator")}
                  <div className="member-info">
                    <div className="member-email">{email}</div>
                    <span className={getRoleBadgeClass("creator")}>
                      Creator
                    </span>
                  </div>
                  <div className="member-actions">
                    <button
                      onClick={() => handleRemoveMember(email, "creator")}
                      className="btn btn-sm btn-danger"
                      title="Remove member"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Readers */}
        {currentTeam.readers && currentTeam.readers.length > 0 && (
          <div className="members-group">
            <h3>Readers</h3>
            <div className="members-list">
              {currentTeam.readers.map((email) => (
                <div key={email} className="member-card">
                  {getRoleIcon("reader")}
                  <div className="member-info">
                    <div className="member-email">{email}</div>
                    <span className={getRoleBadgeClass("reader")}>Reader</span>
                  </div>
                  <div className="member-actions">
                    <button
                      onClick={() => handleRemoveMember(email, "reader")}
                      className="btn btn-sm btn-danger"
                      title="Remove member"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowAddMemberModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Team Member</h3>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder="user@example.com"
                className="input"
              />
            </div>

            <div className="form-group">
              <label>Role</label>
              <select
                value={newMemberRole}
                onChange={(e) => setNewMemberRole(e.target.value)}
                className="select"
              >
                <option value="reader">Reader (View only)</option>
                <option value="creator">
                  Creator (Can upload and generate)
                </option>
              </select>
              <p className="form-hint">
                {newMemberRole === "reader"
                  ? "Can view diagrams and contexts but cannot create or edit"
                  : "Can upload contexts, generate diagrams, and edit results"}
              </p>
            </div>

            <div className="modal-actions">
              <button
                onClick={handleAddMember}
                disabled={adding || !newMemberEmail.trim()}
                className="btn btn-primary"
              >
                {adding ? "Adding..." : "Add Member"}
              </button>
              <button
                onClick={() => setShowAddMemberModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Permissions Reference */}
      <div className="settings-section">
        <h2>Role Permissions</h2>
        <div className="permissions-table">
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th>Owner</th>
                <th>Creator</th>
                <th>Reader</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>View contexts & diagrams</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>Upload contexts</td>
                <td>✓</td>
                <td>✓</td>
                <td>✗</td>
              </tr>
              <tr>
                <td>Generate diagrams</td>
                <td>✓</td>
                <td>✓</td>
                <td>✗</td>
              </tr>
              <tr>
                <td>Edit diagrams</td>
                <td>✓</td>
                <td>✓</td>
                <td>✗</td>
              </tr>
              <tr>
                <td>Delete contexts</td>
                <td>✓</td>
                <td>✗</td>
                <td>✗</td>
              </tr>
              <tr>
                <td>Manage team members</td>
                <td>✓</td>
                <td>✗</td>
                <td>✗</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Team Modal */}
      {showCreateTeamModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCreateTeamModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Team</h3>

            <div className="form-group">
              <label>Team Name</label>
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Enter team name"
                className="input"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Description (optional)</label>
              <textarea
                value={newTeamDescription}
                onChange={(e) => setNewTeamDescription(e.target.value)}
                placeholder="What is this team for?"
                className="textarea"
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button
                onClick={handleCreateTeam}
                disabled={creatingTeam || !newTeamName.trim()}
                className="btn btn-primary"
              >
                {creatingTeam ? "Creating..." : "Create Team"}
              </button>
              <button
                onClick={() => setShowCreateTeamModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
